import Modal from 'react-modal';
import { LuStore } from "react-icons/lu";
import { CircularProgress } from '@mui/material';
import { useGetBusinessByIdQuery } from '../../store/services/businessServices';
import { useGetBusinessWinnersByBusinessQuery } from '../../store/services/businessWinnersServices';
import { useGetMunicipalitiesQuery } from '../../store/services/municipalitiesServices';

const getCustomStyles = (opacity = 0.8) => ({
    content: {
        position: 'static',
        borderRadius: '10px',
        padding: 0,
        maxWidth: 'calc(100vw - 2rem)',
    },
    overlay: {
        background: `rgba(0, 0, 0, ${opacity})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
    }
});

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : null);

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== '';

/* Businesses created from approved receipts only get a NUI, name and activity,
   so fields the record doesn't have are left out instead of shown as "—" */
const Row = ({ label, children, valueClassName = 'font-medium' }) =>
    hasValue(children) ? (
        <div className='flex justify-between items-center gap-4'>
            <div className='text-xs uppercase text-gray-500 min-w-0 shrink-0'>{label}</div>
            <div className={`text-xs text-right break-words min-w-0 ${valueClassName}`}>{children}</div>
        </div>
    ) : null;

Modal.setAppElement(document.getElementById('root'))

/* `business` is the row from the list; the full record is refetched by id. */
export default function BusinessDetails({ business, isOpen, onClose, opacity = 0.8 }) {
    const { data: businessData, isLoading } = useGetBusinessByIdQuery(business?.id, {
        skip: !business?.id,
    })
    const data = businessData?.data || business || {}

    const { data: winsData, isError: winsError } = useGetBusinessWinnersByBusinessQuery(data?.nui, {
        skip: !isOpen || !data?.nui,
    })
    const { data: municipalities } = useGetMunicipalitiesQuery(undefined, { skip: !isOpen })

    const municipalityName = (municipalities?.data || [])
        .find((m) => String(m.municipality_no) === String(data?.municipality_no))?.municipality_name
        || data?.municipality_no

    const hasActivity = hasValue(data?.activity_code) || hasValue(data?.activity_name)
    const hasContact = [data?.address, data?.city, data?.municipality_no, data?.zip_code, data?.phone, data?.email].some(hasValue)

    const wins = Array.isArray(winsData?.data) ? winsData.data : []

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={getCustomStyles(opacity)}
            contentLabel="Detajet e biznesit"
        >
            <div className="bg-white p-5 w-[380px] max-w-full max-h-[90dvh] overflow-y-auto text-sm">
                {isLoading ? <div className="flex justify-center items-center h-[480px] max-h-[80dvh]"><CircularProgress /></div> : (
                    <div className='flex flex-col gap-4 items-center'>
                        <div className='flex flex-col gap-2 items-center'>
                            <div className='w-14 h-14 rounded-full flex items-center justify-center border border-[#bbb]'>
                                <LuStore color='#bbb' size={24} />
                            </div>
                            <div className='text-lg sm:text-xl text-center break-words'>{data?.name || '—'}</div>
                            {data?.type && <div className='text-xs text-gray-500 text-center'>{data.type}</div>}
                        </div>
                        <hr className='w-full' />
                        <div className='flex flex-col gap-2 w-full text-sm'>
                            <Row label='ID'>{data?.id}</Row>
                            <Row label='NUI' valueClassName='font-medium tabular-nums'>{data?.nui}</Row>
                            <Row label='Numri fiskal' valueClassName='font-medium tabular-nums'>{data?.fiscal_number}</Row>
                            <Row label='Data e regjistrimit'>{formatDate(data?.registration_date)}</Row>
                        </div>
                        {hasActivity && (
                            <>
                                <hr className='w-full' />
                                <div className='flex flex-col gap-2 w-full text-sm'>
                                    <Row label='Kodi NAC' valueClassName='font-semibold tabular-nums'>{data?.activity_code}</Row>
                                    <Row label='Aktiviteti'>{data?.activity_name}</Row>
                                </div>
                            </>
                        )}
                        {hasContact && (
                            <>
                                <hr className='w-full' />
                                <div className='flex flex-col gap-2 w-full text-sm'>
                                    <Row label='Adresa'>{data?.address}</Row>
                                    <Row label='Qyteti'>{data?.city}</Row>
                                    <Row label='Komuna'>{municipalityName}</Row>
                                    <Row label='Kodi postar'>{data?.zip_code}</Row>
                                    <Row label='Telefoni'>{data?.phone}</Row>
                                    <Row label='Email'>{data?.email}</Row>
                                </div>
                            </>
                        )}
                        {/* Needs winners.view; hidden when the role can't read business wins */}
                        {!winsError && (
                            <>
                                <hr className='w-full' />
                                <div className='flex flex-col gap-2 w-full text-sm'>
                                    <Row label='Fitore gjithsej'>{wins.length}</Row>
                                </div>
                            </>
                        )}
                        <hr className='w-full' />
                        <div className='flex flex-col gap-2 w-full text-sm'>
                            <Row label='Krijuar'>{formatDate(data?.created_at)}</Row>
                            <Row label='Përditësuar'>{formatDate(data?.updated_at)}</Row>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    )
}
